import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../AxiosInstance";
import { FaRegHandPointDown } from "react-icons/fa6";
import { useUser } from "../../context/UserContext";
import { useAdminUser } from "../../context/AdminUserContext";
import { useAnalytics } from "../../context/AnalyticsContext";
import { ThreeDots } from "react-loader-spinner";

const Login = () => {
  const navigate = useNavigate();
  const { fetchUserData, setClientUser, setWorkerUser } = useUser();
  const { fetchAdminUserData, setAdminUser } = useAdminUser();
  const [loginType, setLoginType] = useState("user");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { trackPageHit } = useAnalytics();

  useEffect(() => {
    if (typeof window !== "undefined") {
      trackPageHit("login");
    }
  }, [trackPageHit]);

  const checkAndRedirectUser = (user) => {
    if (user && user.userType) {
      let path = "";
      if (user.userType === "worker") {
        path = "/account/ps-dashboard";
      } else if (user.userType === "client") {
        path = "/account/fs-dashboard";
      }
      if (path) {
        navigate(path);
      }
    }
  };

  const checkAndRedirectAdminUser = (user) => {
    if (user && user.assignedRole) {
      let path = "";
      if (
        user.assignedRole === "User Admin" ||
        user.assignedRole === "Support Admin"
      ) {
        path = "/account/admin-dashboard";
      } else if (user.assignedRole === "Super Admin") {
        path = "/account/super-admin-dashboard";
      }

      if (path) {
        navigate(path);
      }
    }
  };

  const handleSignUpClick = () => {
    navigate("/register");
  };

  const handleNavigateToPasswordReset = () => {
    navigate("/password-reset");
  };

  // const handleLogin = async (e) => {
  //   e.preventDefault();
  //   setErrorMessage("");
  //   setIsLoading(true);

  //   try {
  //     await axiosInstance.post("/v1/auth/authenticate", {
  //       email,
  //       password,
  //     });

  //     try {
  //       const users = await fetchUserData();
  //       const user = users[0];
  //       console.log("Logging in with user:", user);

  //       if (user && user.emailVerified) {
  //         if (user.userType === "client") {
  //           setClientUser(user);
  //         } else if (user.userType === "worker") {
  //           setWorkerUser(user);
  //         }
  //         checkAndRedirectUser(user);
  //       } else {
  //         setErrorMessage(
  //           "Your email address is not verified. Please verify before logging in."
  //         );
  //       }
  //     } catch (userError) {
  //       console.error("Failed to fetch user details:", userError.message);
  //       setErrorMessage(
  //         "Failed to fetch user details. Please try again later."
  //       );
  //     }
  //   } catch (authError) {
  //     if (authError.response && authError.response.status === 401) {
  //       setErrorMessage("Invalid email or password");
  //     } else {
  //       console.error(
  //         "An error occurred during authentication:",
  //         authError.message
  //       );
  //       setErrorMessage("Authentication error. Please try again later.");
  //     }
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleUserLogin = async () => {
    try {
      await axiosInstance.post("/v1/auth/authenticate", {
        email,
        password,
      });

      try {
        const users = await fetchUserData();
        const user = users[0];
        console.log("Logging in with user:", user);

        if (user && user.emailVerified) {
          if (user.userType === "client") {
            setClientUser(user);
          } else if (user.userType === "worker") {
            setWorkerUser(user);
          }
          checkAndRedirectUser(user);
        } else {
          setErrorMessage(
            "Your email address is not verified. Please verify before logging in."
          );
        }
      } catch (userError) {
        console.error("Failed to fetch user details:", userError.message);
        setErrorMessage(
          "Failed to fetch user details. Please try again later."
        );
      }
    } catch (authError) {
      if (authError.response && authError.response.status === 401) {
        setErrorMessage("Invalid email or password");
      } else {
        console.error(
          "An error occurred during authentication:",
          authError.message
        );
        setErrorMessage("Authentication error. Please try again later.");
      }
    }
  };

  const handleAdminLogin = async () => {
    try {
      await axiosInstance.post("/v1/admin/auth/authenticate", {
        email,
        password,
      });

      try {
        const users = await fetchAdminUserData();
        const user = users[0];
        console.log("Logging in with admin user:", user);

        if (user && user.emailVerified) {
          if (
            user.assignedRole === "User Admin" ||
            user.assignedRole === "Support Admin" ||
            user.assignedRole === "Super Admin"
          ) {
            setAdminUser(user);
          }
          console.log("Admin user data saved in context after login:", user);
          checkAndRedirectAdminUser(user);
        } else {
          setErrorMessage(
            "Your email address is not verified. Please verify before logging in."
          );
        }
      } catch (userError) {
        console.error("Failed to fetch admin user details:", userError.message);
        setErrorMessage(
          "Failed to fetch admin user details. Please try again later."
        );
      }
    } catch (authError) {
      if (authError.response && authError.response.status === 401) {
        setErrorMessage("Invalid email or password");
      } else {
        console.error(
          "An error occurred during admin authentication:",
          authError.message
        );
        setErrorMessage("Authentication error. Please try again later.");
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      if (loginType === "admin") {
        await handleAdminLogin();
      } else {
        await handleUserLogin();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isAdmin = loginType === "admin";

  return (
    <div className="login-wrapper">
      <div className="login-page-container">
        {isLoading && (
          <div className="loading-spinner">
            <ThreeDots
              visible={true}
              height="80"
              width="40"
              color="#4fa94d"
              radius="9"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        )}
        <div
          className={
            isAdmin ? "admnlogin-contents-container" : "login-contents-container"
          }
        >
          <div className="login-tabs">
            <button
              type="button"
              className={`login-tab ${!isAdmin ? "active" : ""}`}
              onClick={() => {
                setLoginType("user");
                setErrorMessage("");
              }}
            >
              User Login
            </button>
            <button
              type="button"
              className={`login-tab ${isAdmin ? "active" : ""}`}
              onClick={() => {
                setLoginType("admin");
                setErrorMessage("");
              }}
            >
              Admin Login
            </button>
          </div>
          <h1>
            {isAdmin ? "LogIn - Administration" : "Log into your account"}
          </h1>
          <div className={`login-error-message ${errorMessage ? "show" : ""}`}>
            {errorMessage}
          </div>

          <form onSubmit={handleLogin}>
            <div className="field-container">
              <label htmlFor="email">User Email</label>
              <span className="login-arrow-indicator">➤</span>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete={isAdmin ? "username" : "email"}
              />
            </div>
            <div className="field-container">
              <label htmlFor="password">Password</label>
              <span className="login-arrow-indicator">➤</span>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={isAdmin ? "current-password" : "password"}
              />
            </div>
            <button type="submit" className="login-submit-btn">
              {isLoading
                ? isAdmin
                  ? "Signing in..."
                  : "Logging in..."
                : "Login"}
            </button>

            <div
              className="signup-prompt-link"
              onClick={!isAdmin ? handleNavigateToPasswordReset : undefined}
              style={isAdmin ? { cursor: "default" } : {}}
            >
              Forgot your password?
            </div>
            {!isAdmin && (
              <div className="signup-prompt">
                <div>Need an account?</div>
                {/* <FaRegHandPointDown className="signup-caret" /> */}
                <div
                  className="signup-prompt-button"
                  onClick={handleSignUpClick}
                >
                  Sign Up
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="admin-help-text">
                Not An administrator? Switch to <strong>User Login</strong>.
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
